import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Chart, registerables } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import 'chartjs-adapter-date-fns';
import api from '../api';
import Navbar from '../components/Navbar';
// Chart.js draws on canvas, so its colours are read from the theme variables when the chart is built.
const css = n => getComputedStyle(document.documentElement).getPropertyValue(n).trim();

Chart.register(...registerables, zoomPlugin);

// ── Palette ───────────────────────────────────────────────────────────────────
const COLORS = [
  '#7C3AED','#059669','#DC2626','#D97706','#2563EB',
  '#DB2777','#0891B2','#65A30D','#9333EA','#EA580C',
  '#0D9488','#B45309','#4F46E5','#BE185D','#047857',
  '#1D4ED8','#7C2D12','#064E3B','#1E1B4B','#78350F',
];

const SOURCE_OPTS = [
  { key: 'c',    label: 'Close' },
  { key: 'e12',  label: 'EMA 12' },
  { key: 'e20',  label: 'EMA 20' },
  { key: 'e50',  label: 'EMA 50' },
  { key: 'e100', label: 'EMA 100' },
  { key: 'e200', label: 'EMA 200' },
];

const EMA_OPTS = [
  { key: 'e12',  label: 'EMA 12' },
  { key: 'e20',  label: 'EMA 20' },
  { key: 'e50',  label: 'EMA 50' },
  { key: 'e100', label: 'EMA 100' },
  { key: 'e200', label: 'EMA 200' },
];

const DAY_MS   = 864e5;
const MONTH_MS = 30.44 * DAY_MS;
const YEAR_MS  = 365.25 * DAY_MS;

function ymToMs(y, m) {
  return y * YEAR_MS + m * MONTH_MS;
}

function msToYM(ms) {
  const months = Math.round(ms / MONTH_MS);
  return { y: Math.floor(months / 12), m: months % 12 };
}

function normalise(points, minT) {
  const visible = points.filter(p => p.x >= minT && p.y0 != null);
  if (!visible.length) return points.map(p => ({ ...p, y: null }));
  const base = visible[0].y0;
  if (base == null || base === 0) return points.map(p => ({ ...p, y: null }));
  return points.map(p => ({
    ...p,
    y: p.x < minT || p.y0 == null ? null : ((p.y0 - base) / base) * 100,
  }));
}

function strategyIndex(series) {
  let value = 1;
  return series.map((d, i) => {
    if (i > 0) {
      const prev = Number(series[i - 1].portfolio_value);
      const current = Number(d.portfolio_value);
      const contribution = Number(d.contribution) || 0;
      const growth = prev > 0 ? (current - contribution) / prev : 1;
      if (Number.isFinite(growth)) value *= growth;
    }
    return { x: new Date(d.d).getTime(), y0: value };
  });
}

// ── Styles ────────────────────────────────────────────────────────────────────
const S = {
  page:         { minHeight: '100vh', background: 'var(--bg)', display: 'flex', flexDirection: 'column' },
  content:      { flex: 1, maxWidth: '1400px', margin: '0 auto', padding: '24px 20px', width: '100%' },
  title:        { fontSize: '20px', fontWeight: 700, color: 'var(--text)', marginBottom: '20px', fontFamily: "'Space Grotesk',sans-serif" },
  topRow:       { display: 'flex', gap: '12px', alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: '16px' },
  searchWrap:   { position: 'relative', flex: '0 0 280px' },
  input:        { width: '100%', padding: '8px 12px', fontSize: '13px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--bg)', color: 'var(--text)', fontFamily: "'JetBrains Mono',monospace", outline: 'none' },
  dropdown:     { position: 'absolute', top: '100%', left: 0, right: 0, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', zIndex: 100, boxShadow: 'var(--shadow)', marginTop: '4px' },
  dropItem:     { padding: '8px 12px', cursor: 'pointer', fontSize: '12px', display: 'flex', gap: '8px', alignItems: 'center', borderBottom: '1px solid var(--border)' },
  dropSym:      { fontFamily: "'JetBrains Mono',monospace", fontWeight: 600, color: 'var(--text)', minWidth: '80px' },
  dropName:     { color: 'var(--text-muted)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  badge:        { fontSize: '10px', fontFamily: "'JetBrains Mono',monospace", padding: '2px 6px', borderRadius: '4px', background: 'var(--accent-dim)', color: 'var(--accent)', fontWeight: 600 },
  btn:          { padding: '8px 14px', fontSize: '12px', fontWeight: 600, border: '1px solid var(--border)', borderRadius: 'var(--radius)', cursor: 'pointer', background: 'var(--surface)', color: 'var(--text)', fontFamily: "'Space Grotesk',sans-serif" },
  listSelect:   { padding: '8px 12px', fontSize: '13px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--bg)', color: 'var(--text)', fontFamily: "'Space Grotesk',sans-serif", outline: 'none', maxWidth: '220px' },
  card:         { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '20px', marginBottom: '16px' },
  chartWrap:    { position: 'relative', height: '520px' },
  controls:     { display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '16px' },
  windowLabel:  { fontSize: '11px', color: 'var(--text-muted)', fontFamily: "'JetBrains Mono',monospace" },
  windowInput:  { width: '46px', padding: '4px 6px', fontSize: '11px', border: '1px solid var(--border)', borderRadius: '4px', background: 'var(--bg)', color: 'var(--text)', fontFamily: "'JetBrains Mono',monospace", outline: 'none' },
  modeToggle:   { display: 'flex', gap: '4px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: '6px', padding: '3px', marginLeft: 'auto' },
  modeBtn:      (active) => ({ padding: '4px 12px', fontSize: '11px', fontWeight: 600, border: 'none', borderRadius: '4px', cursor: 'pointer', fontFamily: "'JetBrains Mono',monospace", background: active ? 'var(--accent)' : 'transparent', color: active ? 'var(--on-accent)' : 'var(--text-muted)' }),
  legendWrap:   { display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '14px' },
  legendItem:   (hovered, anyHovered) => ({
    display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px',
    cursor: 'default', padding: '4px 8px', borderRadius: '5px',
    border: '1px solid var(--border)', background: 'var(--bg)',
    userSelect: 'none',
    opacity: anyHovered && !hovered ? 0.3 : 1,
    transition: 'opacity 0.15s',
  }),
  legendDot:    (color, faded) => ({ width: '10px', height: '10px', borderRadius: '50%', background: color, opacity: faded ? 0.25 : 1, flexShrink: 0, cursor: 'pointer' }),
  emaSection:   { display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap', marginTop: '10px' },
  emaLabel:     { fontSize: '11px', color: 'var(--text-muted)', fontFamily: "'JetBrains Mono',monospace", marginRight: '4px' },
  emaBtn:       (active) => ({ padding: '3px 9px', fontSize: '11px', fontWeight: 600, border: '1px solid var(--border)', borderRadius: '4px', cursor: 'pointer', fontFamily: "'JetBrains Mono',monospace", background: active ? 'var(--accent-dim)' : 'var(--bg)', color: active ? 'var(--accent)' : 'var(--text-muted)' }),
  removeBtn:    { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '14px', padding: '0 2px', lineHeight: 1 },
  tickerList:   { display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '12px' },
  emptyMsg:     { color: 'var(--text-muted)', fontSize: '13px', textAlign: 'center', padding: '60px 0' },
  errorMsg:     { background: 'var(--error-dim)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '10px 14px', color: 'var(--error)', fontSize: '12px', marginBottom: '12px' },
  sourceSelect: { padding: '2px 6px', fontSize: '11px', border: '1px solid var(--border)', borderRadius: '4px', background: 'var(--surface)', color: 'var(--text)', fontFamily: "'JetBrains Mono',monospace", outline: 'none', cursor: 'pointer' },
  colorInput:   { width: '24px', height: '24px', padding: 0, border: '1px solid var(--border)', borderRadius: '5px', background: 'transparent', cursor: 'pointer' },
};

export default function Compare() {
  const canvasRef = useRef(null);
  const chartRef  = useRef(null);

  const [tickers, setTickers]               = useState([]);
  const [seriesData, setSeriesData]         = useState({});
  const [hidden, setHidden]                 = useState({});
  const [tickerSources, setTickerSources]   = useState({});
  const [hoveredTicker, setHoveredTicker]   = useState(null);

  const [query, setQuery]                   = useState('');
  const [results, setResults]               = useState([]);
  const [showDrop, setShowDrop]             = useState(false);
  const searchTimer                         = useRef(null);

  const [lists, setLists]                   = useState([]);
  const [loadingList, setLoadingList]       = useState(false);
  const [strategies, setStrategies]         = useState([]);
  const [selectedStrategies, setSelectedStrategies] = useState([]);
  const [strategyData, setStrategyData]     = useState({});
  const [loadingStrategy, setLoadingStrategy] = useState(false);

  const [winY, setWinY]                     = useState(5);
  const [winM, setWinM]                     = useState(0);
  const [winStart, setWinStart]             = useState(null);
  const [dragWin, setDragWin]               = useState(null);
  const boundsRef                           = useRef({ min: null, max: null });
  const dragRef                             = useRef(null);
  const syncRef                             = useRef(null);

  const [mode, setMode]                     = useState('pct');
  const [activeEmas, setActiveEmas]         = useState([]);
  const [error, setError]                   = useState('');
  const [loading, setLoading]               = useState(false);

  useEffect(() => {
    api.get('/lists').then(r => setLists(r.data.lists || [])).catch(() => {});
    api.get('/strategy/strategies').then(r => setStrategies(r.data.strategies || [])).catch(() => {});
  }, []);

  const handleSearch = (val) => {
    setQuery(val);
    clearTimeout(searchTimer.current);
    if (val.length < 1) { setResults([]); setShowDrop(false); return; }
    searchTimer.current = setTimeout(async () => {
      try {
        const r = await api.get('/chart/search', { params: { q: val, indexes: 1 } });
        setResults(r.data || []);
        setShowDrop(true);
      } catch { setResults([]); }
    }, 200);
  };

  const addTicker = useCallback(async (sym, exch, name, asset_type, colorIndex) => {
    const key = `${sym}:${exch}`;
    if (tickers.find(t => t.key === key)) return;
    const color = COLORS[colorIndex % COLORS.length];
    setLoading(true);
    setError('');
    try {
      const r = await api.get('/compare/series', { params: { tickers: key } });
      const item = r.data.data?.[0];
      if (!item) { setError(`No data for ${key}`); return; }
      setTickers(prev => [...prev, { key, name: name || item.name, asset_type, color }]);
      setSeriesData(prev => ({ ...prev, [key]: item.series }));
      setTickerSources(prev => ({ ...prev, [key]: 'c' }));
    } catch {
      setError(`Failed to load ${key}`);
    } finally {
      setLoading(false);
    }
  }, [tickers]);

  const removeTicker = (key) => {
    setTickers(prev => prev.filter(t => t.key !== key));
    setSeriesData(prev => { const n = { ...prev }; delete n[key]; return n; });
    setHidden(prev => { const n = { ...prev }; delete n[key]; return n; });
    setTickerSources(prev => { const n = { ...prev }; delete n[key]; return n; });
  };

  const loadList = async (listId) => {
    if (!listId) return;
    setLoadingList(true);
    setError('');
    try {
      const r = await api.get(`/lists/${listId}/items`);
      const items = r.data.items || [];
      for (let i = 0; i < items.length; i++) {
        const it = items[i];
        await addTicker(it.symbol, it.exchange, it.name, it.asset_type, tickers.length + i);
      }
    } catch {
      setError('Failed to load list');
    } finally {
      setLoadingList(false);
    }
  };

  const addStrategy = async (strategyId) => {
    if (!strategyId) return;
    const id = Number(strategyId);
    const key = `strategy:${id}`;
    if (selectedStrategies.find(s => s.id === id)) return;
    const strategy = strategies.find(s => s.id === id);
    setLoadingStrategy(true);
    setError('');
    try {
      const r = await api.get(`/strategy/strategies/${id}/backtest/results`);
      const series = r.data.result_json?.series || [];
      if (!series.length) {
        setError(`No completed backtest for ${strategy?.name || 'strategy'}`);
        return;
      }
      const color = COLORS[(tickers.length + selectedStrategies.length) % COLORS.length];
      setSelectedStrategies(prev => [...prev, { id, key, name: strategy?.name || `Strategy ${id}`, color }]);
      setStrategyData(prev => ({ ...prev, [id]: series }));
      setMode('pct');
    } catch (e) {
      setError(e.response?.status === 404
        ? `No completed backtest for ${strategy?.name || 'strategy'}`
        : 'Failed to load strategy');
    } finally {
      setLoadingStrategy(false);
    }
  };

  const removeStrategy = (id) => {
    const key = `strategy:${id}`;
    setSelectedStrategies(prev => prev.filter(s => s.id !== id));
    setStrategyData(prev => { const n = { ...prev }; delete n[id]; return n; });
    setHidden(prev => { const n = { ...prev }; delete n[key]; return n; });
  };

  const getBounds = useCallback(() => {
    let min = null;
    let max = null;
    const add = (a, b) => {
      if (!Number.isFinite(a) || !Number.isFinite(b)) return;
      min = min == null ? a : Math.min(min, a);
      max = max == null ? b : Math.max(max, b);
    };
    tickers.forEach(t => {
      const raw = seriesData[t.key];
      if (raw?.length) add(raw[0].t, raw[raw.length - 1].t);
    });
    selectedStrategies.forEach(s => {
      const raw = strategyData[s.id];
      if (raw?.length) add(new Date(raw[0].d).getTime(), new Date(raw[raw.length - 1].d).getTime());
    });
    return { min, max };
  }, [tickers, seriesData, selectedStrategies, strategyData]);

  const buildDatasets = useCallback((minT) => {
    const datasets = [];
    const effectiveMin = minT ?? 0;
    const isPct = mode === 'pct';
    const lineStyle = (item, key, baseWidth = 2) => {
      const isHovered = hoveredTicker === key;
      const anyHovered = hoveredTicker !== null;
      const width = anyHovered ? (isHovered ? 3 : 1) : baseWidth;
      const alpha = anyHovered && !isHovered ? 0.2 : 1;
      const hex = item.color.replace('#', '');
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      return { borderWidth: width, borderColor: `rgba(${r},${g},${b},${alpha})` };
    };

    for (const t of tickers) {
      const raw = seriesData[t.key];
      if (!raw) continue;
      const sourceKey = tickerSources[t.key] ?? 'c';
      const sourceLabel = SOURCE_OPTS.find(s => s.key === sourceKey)?.label ?? 'Close';
      const style = lineStyle(t, t.key);
      const data = isPct
        ? normalise(raw.map(p => ({ x: p.t, y0: p[sourceKey] })), effectiveMin)
        : raw.map(p => ({ x: p.t, y: p[sourceKey] }));

      datasets.push({
        label: sourceKey === 'c' ? t.key : `${t.key} (${sourceLabel})`,
        data,
        borderColor: style.borderColor,
        backgroundColor: 'transparent',
        borderWidth: style.borderWidth,
        pointRadius: 0,
        pointHoverRadius: 0,
        tension: 0,
        hidden: hidden[t.key],
        _tickerKey: t.key,
      });

      if (!isPct) {
        for (const ema of activeEmas) {
          datasets.push({
            label: `${t.key} ${ema.label}`,
            data: raw.map(p => ({ x: p.t, y: p[ema.key] })),
            borderColor: style.borderColor,
            backgroundColor: 'transparent',
            borderWidth: 1,
            borderDash: [4, 3],
            pointRadius: 0,
            pointHoverRadius: 0,
            tension: 0,
            hidden: hidden[t.key],
            _tickerKey: t.key,
          });
        }
      }
    }

    if (isPct) {
      selectedStrategies.forEach(s => {
        const raw = strategyData[s.id];
        if (!raw?.length) return;
        const style = lineStyle(s, s.key, 2);
        datasets.push({
          label: s.name,
          data: normalise(strategyIndex(raw), effectiveMin),
          borderColor: style.borderColor,
          backgroundColor: 'transparent',
          borderWidth: style.borderWidth,
          pointRadius: 0,
          pointHoverRadius: 0,
          tension: 0,
          hidden: hidden[s.key],
          _strategyId: s.id,
        });
      });
    }

    return datasets;
  }, [tickers, seriesData, selectedStrategies, strategyData, hidden, tickerSources, hoveredTicker, mode, activeEmas]);

  const rebaseLive = useCallback((chart) => {
    const xMin = chart.scales.x.min;
    chart.data.datasets.forEach(ds => {
      const first = ds.data.find(pt => pt.x >= xMin && pt.y0 != null);
      if (!first || first.y0 === 0) return;
      const base = first.y0;
      ds.data.forEach(pt => {
        if (pt.y0 != null) pt.y = pt.x < xMin ? null : ((pt.y0 - base) / base) * 100;
      });
    });
    chart.update('none');
  }, []);

  const syncFromChart = useCallback((chart) => {
    const xMin = chart.scales.x.min;
    const xMax = chart.scales.x.max;
    const { y, m } = msToYM(xMax - xMin);
    const cy = Math.max(0, y);
    const cm = Math.max(0, m);
    setWinY(cy);
    setWinM(cy === 0 && cm === 0 ? 1 : cm);
    setWinStart(xMin);
    if (mode === 'pct') rebaseLive(chart);
  }, [mode, rebaseLive]);
  syncRef.current = syncFromChart;

  const rebuildChart = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; }

    const bounds = getBounds();
    if (bounds.min == null || bounds.max == null) {
      boundsRef.current = { min: null, max: null };
      return;
    }
    boundsRef.current = bounds;

    const widthMs = ymToMs(winY, winM);
    const maxStart = Math.max(bounds.min, bounds.max - widthMs);
    let wStart = winStart ?? bounds.min;
    wStart = Math.min(Math.max(wStart, bounds.min), maxStart);
    const wEnd = wStart + widthMs;
    const xAxisMax = Math.min(bounds.max, wEnd) + DAY_MS;

    const datasets = buildDatasets(wStart);
    if (!datasets.length) return;
    const isPct = mode === 'pct';

    chartRef.current = new Chart(canvas, {
      type: 'line',
      data: { datasets },
      options: {
        animation: false,
        responsive: true,
        maintainAspectRatio: false,
        parsing: false,
        interaction: { mode: 'index', intersect: false },
        scales: {
          x: {
            type: 'time',
            time: { unit: 'month', tooltipFormat: 'dd MMM yyyy' },
            min: wStart,
            max: xAxisMax,
            grid: { color: css('--grid') },
            ticks: { color: css('--text-muted'), font: { family: "'JetBrains Mono',monospace", size: 11 }, maxRotation: 0, autoSkip: true, maxTicksLimit: 10 },
          },
          y: {
            grid: { color: css('--grid') },
            ticks: {
              color: css('--text-muted'),
              font: { family: "'JetBrains Mono',monospace", size: 11 },
              callback: v => isPct ? (v > 0 ? '+' : '') + v.toFixed(1) + '%' : v.toLocaleString(),
            },
          },
        },
        plugins: {
          legend: { display: false },
          tooltip: { enabled: false },
          zoom: {
            pan: { enabled: true, mode: 'x', onPanComplete: ({ chart }) => syncRef.current?.(chart) },
            zoom: {
              wheel: { enabled: true },
              pinch: { enabled: true },
              mode: 'x',
              onZoomComplete: ({ chart }) => syncRef.current?.(chart),
            },
          },
        },
      },
    });
  }, [getBounds, winY, winM, winStart, buildDatasets, mode]);

  useEffect(() => {
    rebuildChart();
    return () => { if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; } };
  }, [rebuildChart]);

  useEffect(() => {
    const bounds = getBounds();
    if (bounds.min != null && winStart == null) setWinStart(bounds.min);
  }, [getBounds, winStart]);

  const setWindowYM = (y, m) => {
    y = Math.max(0, Math.floor(Number(y) || 0));
    m = Math.max(0, Math.floor(Number(m) || 0));
    if (y === 0 && m === 0) m = 1;
    if (m > 11 && y > 0) m = 11;
    setWinY(y);
    setWinM(m);
  };

  const toggleHidden = (key) => setHidden(prev => ({ ...prev, [key]: !prev[key] }));
  const toggleEma = (ema) => setActiveEmas(prev => prev.find(e => e.key === ema.key) ? prev.filter(e => e.key !== ema.key) : [...prev, ema]);
  const setSource = (key, sourceKey) => setTickerSources(prev => ({ ...prev, [key]: sourceKey }));
  const setTickerColor = (key, color) => setTickers(prev => prev.map(t => t.key === key ? { ...t, color } : t));
  const setStrategyColor = (id, color) => setSelectedStrategies(prev => prev.map(s => s.id === id ? { ...s, color } : s));

  const anyHovered = hoveredTicker !== null;

  return (
    <div style={S.page}>
      <Navbar compact />

      <div style={S.content}>
        <div style={S.title}>Compare</div>

        <div style={S.topRow}>
          <div style={S.searchWrap}>
            <input
              style={S.input}
              value={query}
              onChange={e => handleSearch(e.target.value)}
              onBlur={() => setTimeout(() => setShowDrop(false), 150)}
              onFocus={() => results.length && setShowDrop(true)}
              placeholder="Search ticker…"
              spellCheck={false}
              autoCapitalize="characters"
            />
            {showDrop && results.length > 0 && (
              <div style={S.dropdown}>
                {results.map((r, i) => (
                  <div
                    key={i}
                    style={{ ...S.dropItem, background: i % 2 === 0 ? 'var(--surface)' : 'var(--bg)' }}
                    onMouseDown={() => {
                      addTicker(r.symbol, r.exchange, r.name, r.asset_type, tickers.length);
                      setQuery(''); setShowDrop(false); setResults([]);
                    }}
                  >
                    <span style={S.dropSym}>{r.symbol}:{r.exchange}</span>
                    <span style={S.dropName}>{r.name}</span>
                    <span style={S.badge}>{r.exchange}</span>
                    {r.asset_type === 'index' && <span style={S.badge}>IDX</span>}
                  </div>
                ))}
              </div>
            )}
          </div>

          <select
            style={S.listSelect}
            defaultValue=""
            onChange={e => { loadList(e.target.value); e.target.value = ''; }}
            disabled={loadingList}
          >
            <option value="" disabled>{loadingList ? 'Loading…' : 'Load a list…'}</option>
            {lists.map(l => <option key={l.id} value={l.id}>{l.name} ({l.item_count})</option>)}
          </select>

          <select
            style={S.listSelect}
            defaultValue=""
            onChange={e => { addStrategy(e.target.value); e.target.value = ''; }}
            disabled={loadingStrategy}
          >
            <option value="" disabled>{loadingStrategy ? 'Loading…' : 'Add a strategy…'}</option>
            {strategies.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>

          {(tickers.length > 0 || selectedStrategies.length > 0) && (
            <button style={S.btn} onClick={() => {
              setTickers([]);
              setSeriesData({});
              setSelectedStrategies([]);
              setStrategyData({});
              setHidden({});
              setTickerSources({});
              setWinStart(null);
              boundsRef.current = { min: null, max: null };
            }}>
              Clear All
            </button>
          )}
        </div>

        {error && <div style={S.errorMsg}>{error}</div>}

        <div style={S.card}>
          {tickers.length === 0 && selectedStrategies.length === 0 ? (
            <div style={S.emptyMsg}>
              {loading || loadingStrategy ? 'Loading data…' : 'Search for a ticker, load a list, or add a strategy to get started.'}
            </div>
          ) : (
            <>
              <div style={S.controls}>
                <span style={S.windowLabel}>Window</span>
                <input style={S.windowInput} type="number" min="0" value={winY} onChange={e => setWindowYM(e.target.value, winM)} title="Window length: years" />
                <span style={S.windowLabel}>y</span>
                <input style={S.windowInput} type="number" min="0" max="11" value={winM} onChange={e => setWindowYM(winY, e.target.value)} title="Window length: months" />
                <span style={S.windowLabel}>m</span>
                <div style={S.modeToggle}>
                  <button style={S.modeBtn(mode === 'pct')} onClick={() => setMode('pct')}>% Return</button>
                  <button
                    style={{ ...S.modeBtn(mode === 'raw'), opacity: selectedStrategies.length ? 0.4 : 1, cursor: selectedStrategies.length ? 'not-allowed' : 'pointer' }}
                    onClick={() => { if (!selectedStrategies.length) setMode('raw'); }}
                    disabled={selectedStrategies.length > 0}
                    title={selectedStrategies.length ? 'Raw mode is ticker-only. Remove strategies to use it.' : undefined}
                  >
                    Raw
                  </button>
                </div>
              </div>

              {mode === 'raw' && (
                <div style={S.emaSection}>
                  <span style={S.emaLabel}>EMA</span>
                  {EMA_OPTS.map(e => (
                    <button key={e.key} style={S.emaBtn(!!activeEmas.find(a => a.key === e.key))} onClick={() => toggleEma(e)}>
                      {e.label}
                    </button>
                  ))}
                </div>
              )}

              <div style={S.chartWrap}>
                <canvas ref={canvasRef} />
              </div>

              <div style={S.legendWrap}>
                {tickers.map(t => (
                  <div
                    key={t.key}
                    style={S.legendItem(hoveredTicker === t.key, anyHovered)}
                    onMouseEnter={() => setHoveredTicker(t.key)}
                    onMouseLeave={() => setHoveredTicker(null)}
                  >
                    <span
                      style={S.legendDot(t.color, hidden[t.key])}
                      onClick={() => toggleHidden(t.key)}
                      title="Click to show/hide"
                    />
                    <input
                      type="color"
                      value={t.color}
                      onChange={e => setTickerColor(t.key, e.target.value)}
                      onClick={e => e.stopPropagation()}
                      style={S.colorInput}
                      title="Change line colour"
                      aria-label={`Change ${t.key} line colour`}
                    />
                    <span style={{ color: hidden[t.key] ? 'var(--text-muted)' : 'var(--text)', fontFamily: "'JetBrains Mono',monospace", fontSize: '11px', fontWeight: 600 }}>
                      {t.key}
                    </span>
                    <span style={{ color: 'var(--text-muted)', fontFamily: "'Space Grotesk',sans-serif", fontSize: '11px', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {t.name}
                    </span>
                    <select
                      style={S.sourceSelect}
                      value={tickerSources[t.key] ?? 'c'}
                      onChange={e => setSource(t.key, e.target.value)}
                      onClick={e => e.stopPropagation()}
                    >
                      {SOURCE_OPTS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                    </select>
                    <button style={S.removeBtn} onClick={() => removeTicker(t.key)} title="Remove">✕</button>
                  </div>
                ))}
                {selectedStrategies.map(s => (
                  <div
                    key={s.key}
                    style={S.legendItem(hoveredTicker === s.key, anyHovered)}
                    onMouseEnter={() => setHoveredTicker(s.key)}
                    onMouseLeave={() => setHoveredTicker(null)}
                  >
                    <span
                      style={S.legendDot(s.color, hidden[s.key])}
                      onClick={() => toggleHidden(s.key)}
                      title="Click to show/hide"
                    />
                    <input
                      type="color"
                      value={s.color}
                      onChange={e => setStrategyColor(s.id, e.target.value)}
                      onClick={e => e.stopPropagation()}
                      style={S.colorInput}
                      title="Change line colour"
                      aria-label={`Change ${s.name} line colour`}
                    />
                    <span style={{ color: hidden[s.key] ? 'var(--text-muted)' : 'var(--text)', fontFamily: "'Space Grotesk',sans-serif", fontSize: '11px', fontWeight: 700 }}>
                      {s.name}
                    </span>
                    <span style={S.badge}>STRATEGY</span>
                    <button style={S.removeBtn} onClick={() => removeStrategy(s.id)} title="Remove">✕</button>
                  </div>
                ))}
              </div>

              {(() => {
                const b = boundsRef.current;
                if (b.min == null || b.max == null) return null;
                const width = dragWin ? dragWin.width : ymToMs(winY, winM);
                const maxStart = Math.max(b.min, b.max - width);
                const ws = Math.min(Math.max(dragWin ? dragWin.start : (winStart ?? b.min), b.min), maxStart);
                const we = Math.min(ws + width, b.max);
                const total = Math.max(1, b.max - b.min);
                const L = ((ws - b.min) / total) * 100;
                const R = ((we - b.min) / total) * 100;
                const iso = t => new Date(t).toISOString().slice(0, 10);
                const clampStart = (s, w) => Math.min(Math.max(s, b.min), Math.max(b.min, b.max - w));
                const startDrag = (e, dragMode) => {
                  e.preventDefault();
                  const rect = e.currentTarget.parentElement.getBoundingClientRect();
                  const x0 = e.clientX;
                  const ws0 = ws;
                  const w0 = width;
                  const move = ev => {
                    const dx = (ev.clientX - x0) / Math.max(1, rect.width) * total;
                    let s;
                    let w;
                    if (dragMode === 'm') {
                      s = clampStart(ws0 + dx, w0);
                      w = w0;
                    } else {
                      const rs = dragMode === 'l' ? Math.max(b.min, Math.min(ws0 + dx, ws0 + w0)) : ws0;
                      const re = dragMode === 'r' ? Math.min(b.max, Math.max(ws0 + w0 + dx, ws0)) : ws0 + w0;
                      let { y, m } = msToYM(re - rs);
                      if (y === 0 && m === 0) m = 1;
                      w = Math.min(ymToMs(y, m), total);
                      s = clampStart(dragMode === 'l' ? re - w : rs, w);
                    }
                    dragRef.current = { start: s, width: w };
                    setDragWin(dragRef.current);
                  };
                  const up = () => {
                    window.removeEventListener('pointermove', move);
                    window.removeEventListener('pointerup', up);
                    const dw = dragRef.current;
                    dragRef.current = null;
                    setDragWin(null);
                    if (dw) {
                      const { y, m } = msToYM(dw.width);
                      setWindowYM(y, m);
                      setWinStart(dw.start);
                    }
                  };
                  window.addEventListener('pointermove', move);
                  window.addEventListener('pointerup', up);
                };
                const setFrom = v => {
                  const t = Date.parse(v + 'T00:00:00Z');
                  if (Number.isFinite(t)) setWinStart(clampStart(t, width));
                };
                const setTo = v => {
                  const t = Date.parse(v + 'T00:00:00Z');
                  if (!Number.isFinite(t)) return;
                  let { y, m } = msToYM(Math.min(t, b.max) - ws);
                  if (y === 0 && m === 0) m = 1;
                  setWindowYM(y, m);
                  setWinStart(clampStart(ws, ymToMs(y, m)));
                };
                const edge = (dragMode, left) => (
                  <div
                    key={dragMode}
                    onPointerDown={e => startDrag(e, dragMode)}
                    title="Drag to resize the window"
                    style={{ position: 'absolute', top: '-3px', bottom: '-3px', left: `calc(${left}% - 4px)`, width: '8px', cursor: 'ew-resize', touchAction: 'none' }}
                  />
                );
                return (
                  <div style={{ paddingTop: '12px', marginTop: '12px', borderTop: '1px solid var(--border)' }}>
                    <div style={{ position: 'relative', height: '14px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: '4px', userSelect: 'none' }}>
                      <div
                        onPointerDown={e => startDrag(e, 'm')}
                        title="Drag to move the window"
                        style={{ position: 'absolute', top: 0, bottom: 0, left: L + '%', width: Math.max(0, R - L) + '%', background: 'var(--accent-glass)', borderLeft: '2px solid var(--accent)', borderRight: '2px solid var(--accent)', borderRadius: '2px', cursor: 'grab', touchAction: 'none' }}
                      />
                      {edge('l', L)}
                      {edge('r', R)}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginTop: '4px' }}>
                      <span style={{ fontSize: '10px', color: 'var(--text-muted)', fontFamily: "'JetBrains Mono',monospace", whiteSpace: 'nowrap' }}>
                        {new Date(ws).toLocaleDateString('en-AU', { month: 'short', year: 'numeric' })} – {new Date(we).toLocaleDateString('en-AU', { month: 'short', year: 'numeric' })}
                      </span>
                      <span style={{ display: 'flex', gap: '6px', alignItems: 'center', fontSize: '10px', color: 'var(--text-muted)', fontFamily: "'JetBrains Mono',monospace" }}>
                        From <input type="date" style={{ ...S.input, width: 'auto', padding: '2px 4px', fontSize: '11px' }} value={iso(ws)} min={iso(b.min)} max={iso(b.max)} onChange={e => setFrom(e.target.value)} />
                        To <input type="date" style={{ ...S.input, width: 'auto', padding: '2px 4px', fontSize: '11px' }} value={iso(we)} min={iso(b.min)} max={iso(b.max)} onChange={e => setTo(e.target.value)} />
                      </span>
                    </div>
                  </div>
                );
              })()}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
